import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react"

import { Button, Card, Drawer, Form, Input, Checkbox, Skeleton } from "antd"
import { CheckboxChangeEvent } from "antd/es/checkbox"

import { useUserMutation } from "@/hook/Dictionaries/useUserMutation"
import { useUserByIdForAdminQuery } from "@/hook/Dictionaries/useUserQuery"

import { USER_PASSWORD_MINLENGTH, USER_PASSWORD_REGEXP } from "@/types/IUser"

export interface UserChangePasswordFormProps {
  open( id?: string ): void
  close(): void
}

interface UserChangePasswordFormValues {
  newPassword: string
  newPasswordRetry: string
  isNotifyUser: boolean
  isPasswordAutoGenerated: boolean
}

export const UserChangePasswordForm = forwardRef<UserChangePasswordFormProps>((props, ref) => {
  const [form] = Form.useForm<UserChangePasswordFormValues>()
  const [id, setId] = useState<string>("")
  const [isOpen, setIsOpen] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [isAutoGenPassword, setIsAutoGenPassword] = useState(false)

  const { data: user, isLoading } = useUserByIdForAdminQuery(id)
  const { changePasswordMutation } = useUserMutation()

  useImperativeHandle(ref, () => ({
    open(id: string) {
      onOpenHandler(id)
    },
    close() {
      onCloseHandler()
    }
  }))

  const title = useMemo(() => {
    return "Сменить пароль пользователя"
  }, [])

  const onOpenHandler = (id: string) => {
    setId(id)
    form.resetFields()
    setIsOpen(true)
  }

  const onFinish = async (data: UserChangePasswordFormValues) => {
    try {
      setIsProcessing(true)
      const request = {
        id: id,
        password: data.newPassword,
        isNotifyUser: data.isNotifyUser,
        isPasswordAutoGenerated: data.isPasswordAutoGenerated
      }

      await changePasswordMutation.mutateAsync(request)

      form.resetFields()
      onCloseHandler()
    } finally {
      setIsProcessing(false)
    }
  }

  const onCloseHandler = () => {
    setId("")
    form.resetFields()
    setIsOpen(false)
    setIsAutoGenPassword(false)
  }

  const onAutoGenPasswordChange = (e : CheckboxChangeEvent) => {
    setIsAutoGenPassword(e.target.checked)
    if(e.target.checked) {
      form.resetFields(["newPassword", "newPasswordRetry"])
    }
  }

  return (
      <Drawer
          title={title}
          width={820}
          onClose={onCloseHandler}
          open={isOpen}
          styles={{ body: { paddingBottom: 80 } }}
          extra={
            <Button type="primary" disabled={isProcessing} onClick={form.submit}>
              Сохранить
            </Button>
          }
      >
        <Skeleton loading={isLoading}>
        <Card title="Данные пользователя" bordered={false}>
          <p><b>Логин:</b> {user?.response.login}</p>
          <p><b>Фамилия:</b> {user?.response.lastName}</p>
          <p><b>Имя:</b> {user?.response.firstName}</p>
          <p><b>E-mail:</b> {user?.response.email}</p>
        </Card>
        <Form layout="vertical" form={form} onFinish={onFinish}>
          <Form.Item name="isPasswordAutoGenerated" valuePropName="checked">
            <Checkbox onChange={onAutoGenPasswordChange}>Сгенерировать новый пароль автоматически</Checkbox>
          </Form.Item>
          <Form.Item
              label="Новый пароль"
              name="newPassword"
              rules={[
                { required: !isAutoGenPassword, message: "Пожалуйста введите новый пароль" },
                { min: USER_PASSWORD_MINLENGTH, message: `Минимальная длина пароля - ${USER_PASSWORD_MINLENGTH} символов` },
                { pattern: USER_PASSWORD_REGEXP, message: "Пароль должен содержать хотя бы одну заглавную букву и одну цифру" }
              ]}
          >
            <Input.Password disabled={isAutoGenPassword}/>
          </Form.Item>
          <Form.Item
              label="Повторите новый пароль"
              name="newPasswordRetry"
              rules={[
                { required: !isAutoGenPassword, message: "Пожалуйста введите новый пароль" },
                { min: USER_PASSWORD_MINLENGTH, message: `Минимальная длина пароля - ${USER_PASSWORD_MINLENGTH} символов` },
                { pattern: USER_PASSWORD_REGEXP, message: "Пароль должен содержать хотя бы одну заглавную букву и одну цифру" },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue("newPassword") === value) {
                      return Promise.resolve()
                    }
                    return Promise.reject(new Error("Введенные пароли должены совпадать"))
                  }
                })
              ]}
          >
            <Input.Password disabled={isAutoGenPassword} />
          </Form.Item>
          <Form.Item name="isNotifyUser" valuePropName="checked">
            <Checkbox disabled={!user?.response.email}>Уведомить пользователя по e-mail</Checkbox>
          </Form.Item>
        </Form>
        </Skeleton>
      </Drawer>
  )
})
